import { Badge, Box, Button, Flex, Heading, HStack, Tag } from '@chakra-ui/react';
import { Tooltip } from '@/components/ui/tooltip';
import { FiPrinter } from 'react-icons/fi'
import { explainCopy } from '../../lib/explainCopy'
import { quadrantRationale, type QuadrantCell } from '../analyticsMap/bivariate'
import { LeanExplainer } from '../analyticsMap/LeanExplainer'
import { vizDiv } from '../shared/viz'
import {
  UNIT_TYPE_OPTIONS,
  US_STATES,
  type GeoUnitProfile,
  type LeanCyclePoint,
} from '../../types/geoAnalytics'
import ProfileCard from './ProfileCard'

interface ProfileHeaderProps {
  profile: GeoUnitProfile
  /** Bivariate classification, or null when lean/turnout inputs are missing. */
  quadrantCell: QuadrantCell | null
}

function leanBadgeScheme(leanFormatted: string): 'blue' | 'red' | 'gray' {
  if (leanFormatted.startsWith('D')) return 'blue'
  if (leanFormatted.startsWith('R')) return 'red'
  return 'gray'
}

export function ProfileHeader({ profile, quadrantCell }: ProfileHeaderProps) {
  const stateName = US_STATES.find((s) => s.code === profile.state)?.name ?? profile.state
  const unitLabel =
    UNIT_TYPE_OPTIONS.find((u) => u.key === profile.unitType)?.label ?? profile.unitType
  const eyebrow = `${stateName} · ${unitLabel}`

  return (
    <ProfileCard
      eyebrow={eyebrow}
      source="Lean: ORCL rollup of precinct-level presidential results (MEDSL)"
    >
      <Flex justify="space-between" align="flex-start" gap={4} wrap="wrap" mt={1}>
        <Box>
          <Heading size="lg" mb={2}>
            {profile.name}
          </Heading>
          <HStack gap={3} wrap="wrap" align="center">
            {profile.leanFormatted ? (
              <Badge colorPalette={leanBadgeScheme(profile.leanFormatted)} fontSize="sm" px={2} py={0.5}>
                ORCL Lean {profile.leanFormatted}
              </Badge>
            ) : (
              <Badge colorPalette="gray" fontSize="sm" px={2} py={0.5}>
                No lean data
              </Badge>
            )}

            {profile.leanFormatted && (
              <LeanExplainer
                leanFormatted={profile.leanFormatted}
                leanByCycle={profile.leanByCycle}
              />
            )}

            <QuadrantTag cell={quadrantCell} />

            <LeanSparkline points={profile.leanByCycle} />
          </HStack>
        </Box>

        <Button size="sm" variant="outline" onClick={() => window.print()} flexShrink={0}><FiPrinter />Print / Save PDF
                  </Button>
      </Flex>
    </ProfileCard>
  );
}

/**
 * Three-state quadrant chip. A null cell means lean/turnout inputs are missing
 * ("—"); a cell with a null label is a real, computed centrist/mid-turnout result
 * ("No priority action") — the two must not look the same (session-1 finding B2).
 */
function QuadrantTag({ cell }: { cell: QuadrantCell | null }) {
  if (!cell) {
    return (
      <Tooltip
        content="No quadrant yet — this district is missing the lean or turnout data needed to classify it."
        showArrow
      >
        <Tag.Root size="md" colorPalette="gray" cursor="help">
          Quadrant —
        </Tag.Root>
      </Tooltip>
    );
  }

  if (cell.label) {
    return (
      <Tooltip content={explainCopy.quadrantBadge(cell.label)} showArrow>
        <Tag.Root size="md" colorPalette="purple" cursor="help">
          {cell.label}
        </Tag.Root>
      </Tooltip>
    );
  }

  // Computed, but lands in the mid/even band with no strategy label by design.
  return (
    <Tooltip content={quadrantRationale(cell.row, cell.col)} showArrow>
      <Tag.Root size="md" colorPalette="gray" cursor="help">
        No priority action
      </Tag.Root>
    </Tooltip>
  );
}

/** Inline SVG sparkline of the lean-by-cycle trend — same idiom as UnitScorecard's TurnoutSparkline. */
function LeanSparkline({ points }: { points: LeanCyclePoint[] }) {
  if (points.length < 2) return null
  const w = 150
  const h = 34
  const xs = points.map((_, i) => (i / (points.length - 1)) * (w - 12) + 6)
  const values = points.map((p) => p.lean)
  const min = Math.min(...values)
  const max = Math.max(...values)
  const span = Math.max(max - min, 1)
  const ys = values.map((v) => h - 6 - ((v - min) / span) * (h - 12))
  const path = xs.map((x, i) => `${x},${ys[i]}`).join(' ')
  const first = points[0]
  const last = points[points.length - 1]
  const fmt = (v: number) => (v > 0 ? `D+${v.toFixed(1)}` : v < 0 ? `R+${Math.abs(v).toFixed(1)}` : 'EVEN')
  return (
    <svg width={w} height={h} role="img" aria-label={`Lean trend ${first.year} ${fmt(first.lean)} to ${last.year} ${fmt(last.lean)}`}>
      <line x1={6} y1={h / 2} x2={w - 6} y2={h / 2} stroke="var(--chakra-colors-border)" strokeWidth="1" strokeDasharray="2 3" />
      <polyline points={path} fill="none" stroke={vizDiv.pos} strokeWidth="2" />
      {xs.map((x, i) => (
        <circle key={i} cx={x} cy={ys[i]} r={2.5} fill={values[i] >= 0 ? vizDiv.pos : vizDiv.neg} />
      ))}
    </svg>
  )
}
